/**
 * Component for the upcoming bill, used in the Billing page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam Amplify 2025
 */

import {
  AccountUtil,
  SubscriptionFrequency,
  TextUtil,
  TierType,
  Util,
} from '@amplify3/duplo-back-end'
import './billing.css'
import { HorizontalDivider } from './Divider'
import { PlanInvoice } from './Invoice'
import './UpcomingBill.css'

interface UpcomingBillProps {
  className?: string
  invoice?: PlanInvoice
}

const UpcomingBill = (props: UpcomingBillProps) => {
  const fullClassName = `${props.className} upcoming-bill`

  const getDescription = (
    tier: TierType | undefined,
    frequency: SubscriptionFrequency | undefined
  ) => {
    switch (tier) {
      case TierType.PayAsYouGo:
      case TierType.Subscription: {
        if (SubscriptionFrequency.Yearly == frequency) {
          return 'In the yearly plan, off-package credits are charged monthly.'
        } else {
          const date = props.invoice?.nextPeriod
            ? TextUtil.dateToMonthDayWithOrdinal(props.invoice?.nextPeriod)
            : 'an unknown date'
          return `Details of your next monthly billing on ${date}.`
        }
      }

      case TierType.Enterprise:
      case TierType.EnterprisePayAsYouGo:
      case TierType.EnterpriseSubscription:
        return 'Please contact your Sales Representative.'

      case TierType.Free:
        return 'No upcoming invoice.'

      default:
        return ' '
    }
  }

  interface PriceCompProps {
    currency: string
    priceLabel: string
    priceAmount: number
    creditCountLabel?: string
    creditCount?: number
  }

  const OnePriceComp = (props: PriceCompProps) => {
    return (
      <div className="one-price-comp">
        <div className="line-container price-container">
          <div>{props.priceLabel}</div>
          <div>
            {TextUtil.formatPrice(props.priceAmount, props.currency, true)}
          </div>
        </div>

        {undefined !== props.creditCount &&
          undefined !== props.creditCountLabel && (
            <div className="line-container credit-count-container">
              <div>{props.creditCountLabel}</div>
              <div>{Util.ceilTo2Digits(props.creditCount)}</div>
            </div>
          )}
      </div>
    )
  }

  const PriceDetail = ({ invoice }: { invoice: PlanInvoice }) => {
    return (
      <div className="price-detail-container">
        {(TierType.PayAsYouGo == invoice.tier ||
          (TierType.Subscription == invoice.tier &&
            SubscriptionFrequency.Monthly == invoice.frequency) ||
          (TierType.Subscription == invoice.tier &&
            SubscriptionFrequency.Yearly == invoice.frequency &&
            Util.isInMonthEndingWith(invoice.periodEnd))) && (
          <OnePriceComp
            priceLabel="Cost"
            priceAmount={invoice.baseCost}
            creditCountLabel="Number of credits used"
            creditCount={invoice.baseCreditUsedCount}
            currency={invoice.currency}
          />
        )}

        {undefined !== invoice.offPackageCreditUsedCount &&
          undefined !== invoice.offPackageCost && (
            <OnePriceComp
              priceLabel="Off-package"
              priceAmount={invoice.offPackageCost}
              creditCountLabel="Number of additional credits"
              creditCount={invoice.offPackageCreditUsedCount}
              currency={invoice.currency}
            />
          )}

        {AccountUtil.isTierEnterprise(invoice.tier) && (
          <OnePriceComp
            priceLabel="Custom Credit Price"
            priceAmount={0}
            creditCountLabel="Number of credits"
            creditCount={0}
            currency={invoice.currency}
          />
        )}

        {TierType.Free == invoice.tier && (
          <OnePriceComp
            priceLabel="Cost"
            priceAmount={0}
            currency={invoice.currency}
          />
        )}
      </div>
    )
  }

  const EstimatedTotal = ({ invoice }: { invoice: PlanInvoice }) => {
    return (
      <div className="estimated-total">
        <div>Estimated Total</div>
        <div>
          {TextUtil.formatPrice(invoice.totalCost, invoice.currency, true)}
        </div>
      </div>
    )
  }

  return (
    <div className={fullClassName}>
      <div className="upcoming-bill-container-title">
        <div className="billing-component-title">Upcoming Bill</div>
        <div className="upcoming-bill-description">
          {getDescription(props.invoice?.tier, props.invoice?.frequency)}
        </div>
      </div>

      {props.invoice && (
        <>
          <HorizontalDivider />

          <div className="upcoming-bill-container-price-detail">
            <PriceDetail invoice={props.invoice} />
          </div>

          <HorizontalDivider />

          <EstimatedTotal invoice={props.invoice} />
        </>
      )}
    </div>
  )
}

export default UpcomingBill
